import React, { useState } from "react";
import { useQuery } from "react-query";
import { Button, Modal } from "react-bootstrap";
import CustomModal from "../../../components/common/CustomModal";
import AllianceManagerServices from "../../../services/API/AllianceManager";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import Multiselect from "../../../components/common/MultiSelect";
import { ROLES } from "../../../constant/roles";

const hasId = (list, id) => !!list?.some((item) => item?.id == id);

// Eligible = active, mapped to the SRN's license and covering its
// state, city & municipal (same rule as vendor eligibility)
const isEligible = (item, licenseId, srn) =>
  item?.is_enabled == 1 &&
  hasId(item?.license_names, licenseId) &&
  hasId(item?.states, srn?.state_id) &&
  hasId(item?.cities, srn?.city_id) &&
  hasId(item?.municipals, srn?.municipal_id);

/**
 * Alliance Manager reassignment: pick vendor -> eligible executive -> eligible
 * field executive, with a single Assign button that targets the deepest
 * level selected.
 *
 * srn: { id, srn_name, vendor_id, executive_id, state_id, city_id, municipal_id }
 * vendorsList: vendors already filtered for this SRN's license & location
 */
const ReassignModal = ({ srn, vendorsList, licenseId, onClose, onSuccess }) => {
  const [vendor, setVendor] = useState(() =>
    vendorsList?.find((item) => item?.id == srn?.vendor_id)
  );
  const [executive, setExecutive] = useState();
  const [FExecutive, setFExecutive] = useState();
  // Pending assignment awaiting confirmation: { user, roleId }
  const [confirm, setConfirm] = useState(null);
  const [loader, setLoader] = useState(false);

  const { data: ExecutivesList, isFetching: executivesLoading } = useQuery(
    ["/api/executive", { vendorIds: [vendor?.id] }],
    AllianceManagerServices.getRQExecutiveList,
    { enabled: !!vendor?.id, refetchOnWindowFocus: false }
  );

  const { data: FExecutivesList, isFetching: FExecutivesLoading } = useQuery(
    ["/api/field_executive", { executiveIds: [executive?.id] }],
    AllianceManagerServices.getRQFieldExecutiveList,
    { enabled: !!executive?.id, refetchOnWindowFocus: false }
  );

  // Assigns the SRN to one level; returns false (and toasts) on failure
  const assign = async (userId, userRoleId) => {
    const resp = await AllianceManagerServices.srnReassignToVendor({
      service_ids: [srn?.id],
      vendorId: userId,
      user_role_id: userRoleId,
    });
    if (resp?.status && resp.status != "success") {
      toast.error(resp.message);
      return false;
    }
    return !!resp;
  };

  const reassign = async () => {
    const { user, roleId } = confirm;
    setLoader(true);
    try {
      // When assigning below vendor level, first move the parent levels that
      // differ from the current assignment so vendor -> executive -> FE stay consistent
      const isBelowVendor = roleId !== undefined;
      if (isBelowVendor && vendor?.id != srn?.vendor_id) {
        if (!(await assign(vendor.id))) return;
      }
      if (
        roleId === ROLES.fieldExecutive &&
        executive?.id != srn?.executive_id
      ) {
        if (!(await assign(executive.id, ROLES.executive))) return;
      }
      if (!(await assign(user.id, roleId))) return;

      toast.success(`Reassigned to ${user.name} Successfully`);
      onSuccess();
    } finally {
      setLoader(false);
      setConfirm(null);
    }
  };

  // Single Assign targets the deepest selected level: FE > executive > vendor
  const target = FExecutive?.id
    ? { user: FExecutive, roleId: ROLES.fieldExecutive }
    : executive?.id
    ? { user: executive, roleId: ROLES.executive }
    : vendor?.id
    ? { user: vendor }
    : null;

  const renderLevel = ({
    label,
    options,
    value,
    onChange,
    disabled,
    isLoading,
    placeholder,
  }) => (
    <div className="mb-4">
      <label>{label}</label>
      <Multiselect
        isMulti={false}
        options={options}
        value={value}
        isSearchable
        isClearable
        isDisabled={disabled || isLoading}
        isLoading={isLoading}
        loadingMessage={() => "Loading..."}
        getOptionLabel={(option) => option?.name}
        getOptionValue={(option) => option?.id}
        onChange={onChange}
        menuPosition="fixed"
        placeholder={placeholder}
      />
    </div>
  );

  const footer = (
    <Modal.Footer className="d-flex justify-content-end align-items-center">
      {confirm ? (
        <>
          <span className="me-auto">
            Are you sure you want to reassign this to {confirm.user?.name}?
          </span>
          <Button
            variant="danger"
            onClick={() => setConfirm(null)}
            disabled={loader}
          >
            No
          </Button>
          <Button variant="success" onClick={reassign} disabled={loader}>
            {loader ? <Loader /> : "Yes"}
          </Button>
        </>
      ) : (
        <Button
          variant="success"
          disabled={!target}
          onClick={() => setConfirm(target)}
        >
          Assign
        </Button>
      )}
    </Modal.Footer>
  );

  return (
    <CustomModal
      show
      headerTitle={`Reassign ${srn?.srn_name || ""}`}
      onClose={() => !loader && onClose()}
      footer={footer}
    >
      {renderLevel({
        label: "Vendor",
        options: vendorsList,
        value: vendor,
        placeholder: "Select vendor",
        onChange: (option) => {
          // Executive / FE belong to a vendor, so reset them on vendor change
          setVendor(option);
          setExecutive(null);
          setFExecutive(null);
          setConfirm(null);
        },
      })}
      {renderLevel({
        label: "Executive",
        options: ExecutivesList?.data?.filter((item) =>
          isEligible(item, licenseId, srn)
        ),
        value: executive,
        disabled: !vendor?.id,
        isLoading: executivesLoading,
        placeholder: "Select executive",
        onChange: (option) => {
          setExecutive(option);
          setFExecutive(null);
          setConfirm(null);
        },
      })}
      {renderLevel({
        label: "Field Executive",
        options: FExecutivesList?.data?.filter((item) =>
          isEligible(item, licenseId, srn)
        ),
        value: FExecutive,
        disabled: !executive?.id,
        isLoading: FExecutivesLoading,
        placeholder: "Select field executive",
        onChange: (option) => {
          setFExecutive(option);
          setConfirm(null);
        },
      })}
    </CustomModal>
  );
};

export default ReassignModal;
